import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TableChartIcon from '@mui/icons-material/TableChart';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { ToolLayout } from '../ToolLayout';
import { Marked } from 'marked';
import mermaid from 'mermaid';

// Marked instance with mermaid fenced-code-block support.
// ```mermaid ... ``` blocks are emitted as <div class="mermaid"> so that
// mermaid.run() can process them after the preview DOM is updated.
const mdParser = new Marked();
mdParser.use({
  renderer: {
    code(token: any): string | false {
      if (token.lang === 'mermaid') return `<div class="mermaid">${token.text}</div>`;
      return false; // fall through to default renderer
    },
  },
});

const DEFAULT_MD = `# Hello, Markdown!

Write your **Markdown** here and see the rendered preview live on the right.

## Features

- Live preview as you type
- Copy source or download as \`.md\`
- Syntax highlighting for code blocks

## Example

\`\`\`typescript
const greet = (name: string) => \`Hello, \${name}!\`;
console.log(greet('World'));
\`\`\`

> Blockquotes work too.

---

[OneAdvanced](https://oneadvanced.com)
`;

// ── Toolbar action types ──────────────────────────────────────────────────────
type FormatAction =
  | 'h1' | 'h2' | 'h3'
  | 'bold' | 'italic' | 'strikethrough'
  | 'code' | 'codeblock'
  | 'blockquote'
  | 'ul' | 'ol'
  | 'link' | 'image'
  | 'hr' | 'table';

// Wraps the selected text (or placeholder) with a prefix and suffix
function applyInlineWrap(
  text: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string,
  placeholder: string,
): { newText: string; newStart: number; newEnd: number } {
  const selected = text.substring(start, end) || placeholder;
  const newText = text.substring(0, start) + prefix + selected + suffix + text.substring(end);
  const newStart = start + prefix.length;
  const newEnd = newStart + selected.length;
  return { newText, newStart, newEnd };
}

// Adds/removes a line prefix on every line in the selection (or current line)
function applyLinePrefix(
  text: string,
  start: number,
  end: number,
  prefix: string,
): { newText: string; newStart: number; newEnd: number } {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  let lineEnd: number;
  if (end !== start) lineEnd = end;
  else { const nl = text.indexOf('\n', start); lineEnd = nl === -1 ? text.length : nl; }
  const block = text.substring(lineStart, lineEnd);
  const lines = block.split('\n');
  const allPrefixed = lines.every(l => l.startsWith(prefix));
  const newBlock = allPrefixed
    ? lines.map(l => l.substring(prefix.length)).join('\n')
    : lines.map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n');
  const delta = newBlock.length - block.length;
  return {
    newText: text.substring(0, lineStart) + newBlock + text.substring(lineEnd),
    newStart: start + (allPrefixed ? -prefix.length : prefix.length),
    newEnd: end + delta,
  };
}

// Inserts a block snippet at the cursor, wrapped with blank lines
function insertBlock(
  text: string,
  start: number,
  snippet: string,
): { newText: string; newStart: number; newEnd: number } {
  const needsBefore = start > 0 && text[start - 1] !== '\n';
  const prefix = needsBefore ? '\n\n' : '\n';
  const insertion = `${prefix}${snippet}\n`;
  const newText = `${text.substring(0, start)}${insertion}${text.substring(start)}`;
  const cursorPos = start + insertion.length;
  return { newText, newStart: cursorPos, newEnd: cursorPos };
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    height: 'calc(100vh - 270px)',
    minHeight: 400,
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    flexShrink: 0,
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.5, 0.75),
    height: 40,
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: 'none',
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  },
  toolbarDivider: {
    height: 20,
    margin: theme.spacing(0, 0.5),
  },
  headingBtn: {
    minWidth: 0,
    padding: theme.spacing(0.25, 0.75),
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'inherit',
    lineHeight: 1,
    textTransform: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    },
  },
  textarea: {
    flex: 1,
    resize: 'none',
    padding: theme.spacing(1.5),
    fontFamily: '"Fira Code", "Consolas", monospace',
    fontSize: 13,
    lineHeight: 1.6,
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    outline: 'none',
    boxSizing: 'border-box',
    height: '100%',
  },
  editorBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    border: `1px solid ${theme.palette.divider}`,
    borderTop: 'none',
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  lineGutter: {
    width: 44,
    flexShrink: 0,
    overflow: 'hidden',
    userSelect: 'none',
    padding: theme.spacing(1.5, 0.75, 1.5, 0.5),
    fontFamily: '"Fira Code", "Consolas", monospace',
    fontSize: 13,
    lineHeight: 1.6,
    color: theme.palette.text.disabled,
    textAlign: 'right',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    whiteSpace: 'pre',
    boxSizing: 'border-box',
  },
  preview: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      fontWeight: 700,
      lineHeight: 1.3,
    },
    '& h1': { fontSize: '1.8em', borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: theme.spacing(0.5) },
    '& h2': { fontSize: '1.4em', borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: theme.spacing(0.5) },
    '& p': { marginBottom: theme.spacing(1.5), lineHeight: 1.7 },
    '& ul, & ol': { paddingLeft: theme.spacing(3), marginBottom: theme.spacing(1.5) },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& code': {
      fontFamily: '"Fira Code", monospace',
      fontSize: '0.875em',
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      padding: '2px 5px',
      borderRadius: 3,
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      overflow: 'auto',
      marginBottom: theme.spacing(1.5),
    },
    '& pre code': { backgroundColor: 'transparent', padding: 0 },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: 0,
      paddingLeft: theme.spacing(2),
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1.5),
    },
    '& a': { color: theme.palette.primary.main },
    '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, margin: `${theme.spacing(2)} 0` },
    '& table': { borderCollapse: 'collapse', width: '100%', marginBottom: theme.spacing(1.5) },
    '& th, & td': { border: `1px solid ${theme.palette.divider}`, padding: theme.spacing(0.75, 1.5) },
    '& th': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', fontWeight: 700 },
    '& .mermaid': {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: theme.spacing(1.5),
      '& svg': { maxWidth: '100%', height: 'auto' },
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },

}));

export function MarkdownEditorPage() {
  const { classes } = useStyles();
  const { mode: paletteMode } = useTheme().palette;
  const [source, setSource] = useState('');
  const [html, setHtml] = useState('');
  const [copied, setCopied] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);

  // ── History ──────────────────────────────────────────────────────────────────
  const undoStack = useRef<string[]>([]);
  const redoStack = useRef<string[]>([]);
  // Snapshot of source at the start of the current typing burst (before debounce fires)
  const preTypingSnapshot = useRef('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Push `value` onto the undo stack and clear the redo stack
  const pushUndo = useCallback((value: string) => {
    const stack = undoStack.current;
    if (stack.length > 0 && stack[stack.length - 1] === value) return; // dedup
    undoStack.current = [...stack, value].slice(-100);
    redoStack.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    const prev = undoStack.current[undoStack.current.length - 1];
    redoStack.current = [source, ...redoStack.current];
    undoStack.current = undoStack.current.slice(0, -1);
    preTypingSnapshot.current = prev;
    setSource(prev);
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [source]);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    const next = redoStack.current[0];
    undoStack.current = [...undoStack.current, source];
    redoStack.current = redoStack.current.slice(1);
    preTypingSnapshot.current = next;
    setSource(next);
    setCanUndo(true);
    setCanRedo(redoStack.current.length > 0);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [source]);

  // Called on every textarea keystroke — debounces history commits
  const handleSourceChange = useCallback((value: string) => {
    setSource(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const snapshot = preTypingSnapshot.current;
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      const stack = undoStack.current;
      if (stack.length === 0 || stack[stack.length - 1] !== snapshot) {
        undoStack.current = [...stack, snapshot].slice(-100);
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      }
      preTypingSnapshot.current = value;
    }, 500);
  }, []);

  // ── Render Markdown (race-condition safe) ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const result = mdParser.parse(source);
    if (result instanceof Promise) {
      result.then(h => { if (!cancelled) setHtml(h); });
    } else {
      setHtml(result);
    }
    return () => { cancelled = true; };
  // paletteMode included so theme changes re-parse the source, producing fresh
  // unprocessed .mermaid divs that mermaid.run() can re-render with the new theme.
  }, [source, paletteMode]);

  // ── Mermaid ───────────────────────────────────────────────────────────────
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: paletteMode === 'dark' ? 'dark' : 'default',
      securityLevel: 'loose',
    });
  // Must run before the html effect re-renders .mermaid nodes — declared first
  // so React executes it first within the same flush.
  }, [paletteMode]);

  useEffect(() => {
    if (!previewRef.current) return;
    const nodes = Array.from(previewRef.current.querySelectorAll<HTMLElement>('.mermaid'));
    if (!nodes.length) return;
    mermaid.run({ nodes, suppressErrors: true });
  }, [html]);

  // ── Core toolbar action handler ─────────────────────────────────────────────
  const applyFormat = useCallback((action: FormatAction) => {
    const el = textareaRef.current;
    if (!el) return;

    // Flush any pending debounce and commit current state to history
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
      pushUndo(preTypingSnapshot.current);
    }
    pushUndo(source);
    preTypingSnapshot.current = source;

    const start = el.selectionStart;
    const end = el.selectionEnd;
    let result: { newText: string; newStart: number; newEnd: number };

    switch (action) {
      case 'h1': result = applyLinePrefix(source, start, end, '# '); break;
      case 'h2': result = applyLinePrefix(source, start, end, '## '); break;
      case 'h3': result = applyLinePrefix(source, start, end, '### '); break;
      case 'bold': result = applyInlineWrap(source, start, end, '**', '**', 'bold text'); break;
      case 'italic': result = applyInlineWrap(source, start, end, '*', '*', 'italic text'); break;
      case 'strikethrough': result = applyInlineWrap(source, start, end, '~~', '~~', 'strikethrough'); break;
      case 'code': result = applyInlineWrap(source, start, end, '`', '`', 'code'); break;
      case 'codeblock': result = insertBlock(source, start, `\`\`\`\n${source.substring(start, end) || 'code here'}\n\`\`\``); break;
      case 'blockquote': result = applyLinePrefix(source, start, end, '> '); break;
      case 'ul': result = applyLinePrefix(source, start, end, '- '); break;
      case 'ol': result = applyLinePrefix(source, start, end, '1. '); break;
      case 'link': {
        const sel = source.substring(start, end);
        result = applyInlineWrap(source, start, end, '[', '](https://)', sel || 'link text');
        break;
      }
      case 'image': {
        const sel = source.substring(start, end);
        result = applyInlineWrap(source, start, end, '![', '](https://)', sel || 'alt text');
        break;
      }
      case 'hr': result = insertBlock(source, start, '---'); break;
      case 'table':
        result = insertBlock(
          source,
          start,
          '| Header 1 | Header 2 | Header 3 |\n| --- | --- | --- |\n| Cell 1 | Cell 2 | Cell 3 |\n| Cell 4 | Cell 5 | Cell 6 |',
        );
        break;
      default: return;
    }

    setSource(result.newText);
    preTypingSnapshot.current = result.newText;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.newStart, result.newEnd);
    });
  }, [source, pushUndo]);

  const copySource = useCallback(() => {
    navigator.clipboard.writeText(source);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [source]);

  const downloadMd = useCallback(() => {
    const blob = new Blob([source], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'document.md';
    a.click();
    URL.revokeObjectURL(url);
  }, [source]);

  const clearSource = useCallback(() => {
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    pushUndo(source);
    preTypingSnapshot.current = '';
    setSource('');
  }, [source, pushUndo]);

  const loadSample = useCallback(() => {
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    pushUndo(source);
    preTypingSnapshot.current = DEFAULT_MD;
    setSource(DEFAULT_MD);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [source, pushUndo]);

  const handleScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    const textarea = textareaRef.current;
    const preview = previewRef.current;
    const gutter = gutterRef.current;
    if (!textarea || !preview) return;
    const scrollable = textarea.scrollHeight - textarea.clientHeight;
    if (scrollable <= 0) return;
    const ratio = textarea.scrollTop / scrollable;
    isSyncingScroll.current = true;
    if (gutter) gutter.scrollTop = textarea.scrollTop;
    preview.scrollTop = ratio * (preview.scrollHeight - preview.clientHeight);
    requestAnimationFrame(() => { isSyncingScroll.current = false; });
  }, []);

  const handlePreviewScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    const textarea = textareaRef.current;
    const preview = previewRef.current;
    const gutter = gutterRef.current;
    if (!textarea || !preview) return;
    const scrollable = preview.scrollHeight - preview.clientHeight;
    if (scrollable <= 0) return;
    const ratio = preview.scrollTop / scrollable;
    isSyncingScroll.current = true;
    textarea.scrollTop = ratio * (textarea.scrollHeight - textarea.clientHeight);
    if (gutter) gutter.scrollTop = textarea.scrollTop;
    requestAnimationFrame(() => { isSyncingScroll.current = false; });
  }, []);

  const wordCount = source.trim() ? source.trim().split(/\s+/).length : 0;
  const charCount = source.length;
  const lineCount = source.split('\n').length;
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');

  return (
    <ToolLayout
      title="Markdown Editor"
      subtitle="Live split-pane editor with rendered preview"
    >
      <Box className={classes.root}>
        {/* Editor pane */}
        <Box className={classes.pane}>
          <Box className={classes.paneHeader}>
            <Typography className={classes.paneLabel}>Source</Typography>
            <Box className={classes.actions}>
              <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12, mr: 0.5 }}>
                Load sample
              </Button>
              <Tooltip title={copied ? 'Copied!' : 'Copy Markdown'}>
                <IconButton size="small" onClick={copySource}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download as .md">
                <IconButton size="small" onClick={downloadMd}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear">
                <IconButton size="small" onClick={clearSource}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Formatting toolbar */}
          <Box className={classes.toolbar}>
            {/* Undo / Redo */}
            <Tooltip title="Undo (Ctrl+Z)">
              <span>
                <IconButton size="small" onClick={undo} disabled={!canUndo}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z)">
              <span>
                <IconButton size="small" onClick={redo} disabled={!canRedo}>
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Headings */}
            <Tooltip title="Heading 1">
              <Button className={classes.headingBtn} onClick={() => applyFormat('h1')}>H1</Button>
            </Tooltip>
            <Tooltip title="Heading 2">
              <Button className={classes.headingBtn} onClick={() => applyFormat('h2')}>H2</Button>
            </Tooltip>
            <Tooltip title="Heading 3">
              <Button className={classes.headingBtn} onClick={() => applyFormat('h3')}>H3</Button>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Inline formatting */}
            <Tooltip title="Bold (Ctrl+B)">
              <IconButton size="small" onClick={() => applyFormat('bold')}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton size="small" onClick={() => applyFormat('italic')}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Strikethrough">
              <IconButton size="small" onClick={() => applyFormat('strikethrough')}>
                <StrikethroughSIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Code */}
            <Tooltip title="Inline code">
              <IconButton size="small" onClick={() => applyFormat('code')}>
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Code block">
              <IconButton size="small" onClick={() => applyFormat('codeblock')}>
                <TerminalIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Block elements */}
            <Tooltip title="Blockquote">
              <IconButton size="small" onClick={() => applyFormat('blockquote')}>
                <FormatQuoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bullet list">
              <IconButton size="small" onClick={() => applyFormat('ul')}>
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered list">
              <IconButton size="small" onClick={() => applyFormat('ol')}>
                <FormatListNumberedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Links & media */}
            <Tooltip title="Link">
              <IconButton size="small" onClick={() => applyFormat('link')}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Image">
              <IconButton size="small" onClick={() => applyFormat('image')}>
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

            {/* Misc */}
            <Tooltip title="Horizontal rule">
              <IconButton size="small" onClick={() => applyFormat('hr')}>
                <HorizontalRuleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Table">
              <IconButton size="small" onClick={() => applyFormat('table')}>
                <TableChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Editor body: line numbers + textarea */}
          <Box className={classes.editorBody}>
            <Box ref={gutterRef} className={classes.lineGutter}>
              {lineNumbers}
            </Box>
            <textarea
              ref={textareaRef}
              className={classes.textarea}
              value={source}
              onChange={e => handleSourceChange(e.target.value)}
              onScroll={handleScroll}
              onKeyDown={e => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const el = textareaRef.current!;
                  const start = el.selectionStart;
                  const end = el.selectionEnd;
                  const newValue = `${source.substring(0, start)}  ${source.substring(end)}`;
                  handleSourceChange(newValue);
                  requestAnimationFrame(() => el.setSelectionRange(start + 2, start + 2));
                } else if (e.ctrlKey && !e.shiftKey && e.key === 'z') { e.preventDefault(); undo(); }
                else if (e.ctrlKey && e.shiftKey && e.key === 'Z') { e.preventDefault(); redo(); }
                else if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo(); }
              }}
              spellCheck={false}
              placeholder="Write Markdown here…"
            />
          </Box>
          {/* Status bar — under the editor */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 0.5, minHeight: 20 }}>
            <Typography variant="caption" color="textSecondary">
              {lineCount} {lineCount === 1 ? 'line' : 'lines'} · {wordCount} {wordCount === 1 ? 'word' : 'words'} · {charCount} {charCount === 1 ? 'char' : 'chars'}
            </Typography>
          </Box>
        </Box>

        {/* Preview pane */}
        <Box className={classes.pane}>
          <Box className={classes.paneHeader}>
            <Typography className={classes.paneLabel}>Preview</Typography>
          </Box>
          {/* Spacer matching toolbar height so Paper top aligns with editorBody top */}
          <Box sx={{ height: 40, flexShrink: 0 }} />
          <Paper
            ref={previewRef}
            className={classes.preview}
            elevation={0}
            onScroll={handlePreviewScroll}
            // This is a developer tool — users paste their own Markdown.
            // The rendered HTML is displayed as-is for developer inspection.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {/* Spacer matching the stats bar height so bottom edges align */}
          <Box sx={{ mt: 0.5, minHeight: 20 }} />
        </Box>
      </Box>
    </ToolLayout>
  );
}
